<template>
  <Error503 v-if="globalStore.showMaintenance" />
  <router-view v-else-if="showPlatform && !is_logging_out" />
  <div v-else class="h-screen w-full flex items-center justify-center">
    <Spinner width="100" class="fill-brand" />
    {{ is_logging_out ? $t('ui.common.signing_out') : $t('ui.common.loading') }}
  </div>

  <SnackBar />

  <FAQAsk v-if="showFAQ" />
  <BugReporting />
  <SessionWarningModal
    :show="showWarningModal"
    @close="handleSessionExtended"
    @expired="handleSessionExpired"
  />
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { mapState } from 'pinia'

import Spinner from '@/assets/loaders/Spinner.svg?component'
import BugReporting from '@/components/misc/BugReporting'
import FAQAsk from '@/components/misc/FAQAsk'
import SessionWarningModal from '@/components/misc/SessionWarningModal'
import SnackBar from '@/components/misc/SnackBar'
import Error503 from '@/layouts/503.vue'

import { useBackgroundPoll } from '@/composables/background_poll'
import { useBroadcast } from '@/composables/broadcast'
import { useColorScheme } from '@/composables/color_scheme'
import { useSessionEffects } from '@/composables/session_effects'
import { useTestMode } from '@/composables/test_mode'
import { EVENT_LOGGED_OUT } from '@/constants/broadcast'
import { REFRESH_TOKEN_EXPIRATION_KEY } from '@/constants/session'
import serviceWorker from '@/mixins/serviceWorker'
import { markReady, setupRoutes, getRedirectRouteIfNoAccess } from '@/router'
import ApiService from '@/services/api.service'
import TokenService from '@/services/user/token'
import UserService from '@/services/user/user'
import { useGlobalStore } from '@/stores/global'
import { useSettingsGeneralStore } from '@/stores/settings-general'
import { useUserStore } from '@/stores/user'

import type { Entity, Root } from '@/types/services/root'
import type { Currency } from '@/types/services/global'
import type { SessionRole } from './types/services/user'

export default defineComponent({
  components: {
    BugReporting,
    FAQAsk,
    Error503,
    SessionWarningModal,
    Spinner,
    SnackBar,
  },

  mixins: [serviceWorker],

  setup() {
    const { themeClass } = useColorScheme()

    const globalStore = useGlobalStore()
    const settingsGeneralStore = useSettingsGeneralStore()
    const userStore = useUserStore()

    const broadcast = useBroadcast()
    const testMode = useTestMode()

    useSessionEffects()

    async function fetchCurrencyRatios() {
      if (!userStore.session_started) {
        return
      }

      let entityId: string | null = null
      let rootUserId: string | null = null

      // Assistant: always use the selected client's root_user, not the assistant's own
      if (userStore.currentView === 'assistant') {
        const activeClient = userStore.activeAssistantClient
        if (activeClient) {
          entityId = userStore.entity?.id ?? null
          rootUserId = activeClient.root_user.id
        }
      }
      // Selected entity (admin/superadmin or client/referral with specific entity)
      else if (userStore.entity && userStore.root_user) {
        entityId = userStore.entity.id
        rootUserId = userStore.root_user.id
      }
      // Fallback - client/referral view without selected entity
      else if (userStore.currentView === 'client') {
        const first = userStore.allClientEntitiesWithDetails[0]
        if (first) {
          entityId = first.entity.id
          rootUserId = first.rootUser.id
        }
      } else if (userStore.currentView === 'referral') {
        const first = userStore.allReferrerEntitiesWithRootUser[0]
        if (first) {
          entityId = first.entity.id
          rootUserId = first.rootUser.id
        }
      }

      if (!entityId || !rootUserId) {
        return
      }

      await globalStore.getCurrencyRatios(entityId, rootUserId)
    }

    useBackgroundPoll(fetchCurrencyRatios, 10 * 60 * 1000)

    return {
      themeClass,
      globalStore,
      settingsGeneralStore,
      userStore,
      broadcast,
      testMode,
      fetchCurrencyRatios,
    }
  },
  data() {
    return {
      routesReady: false,
      showWarningModal: false,
      sessionInterval: null as ReturnType<typeof setInterval> | null,
    }
  },

  computed: {
    ...mapState(useGlobalStore, ['allCurrencies', 'generalLoaded']),
    ...mapState(useUserStore, [
      'initial_auth_check_complete',
      'session_started',
      'is_logging_out',
      'root',
      'packages',
      'permissions',
      'currencyCrypto',
      'currencyFiat',
      'currencyHash',
      'entities',
      'entity',
      'hasPackage',
      'currentView',
    ]),
    ...mapState(useUserStore, {
      theme: (store) => store.settings.additional.theme,
    }),
    anyPackages(): boolean {
      return this.packages.length > 0
    },
    showFAQ(): boolean {
      return this.userStore.currentView == 'admin'
    },
    showPlatform(): boolean {
      if (!this.initial_auth_check_complete) return false
      if (!this.routesReady) return false
      if (!this.session_started) return true
      return this.generalLoaded
    },
  },

  watch: {
    themeClass: {
      immediate: true,
      handler(newTheme: string, oldTheme: string) {
        const el = document.documentElement
        el.classList.remove(oldTheme)
        el.classList.add(newTheme)
      },
    },
    root: {
      handler(next: Root | null, prev: Root | null) {
        if (JSON.stringify(next) !== JSON.stringify(prev)) {
          this.$nextTick(() => {
            this.$bus.emit('rootChanged')
          })
        }
      },
    },
    entity: {
      immediate: true,
      handler(next: Entity | null, prev: Entity | null) {
        this.handlerEntityChanged(next, prev)
      },
    },
    currencyHash: {
      handler(next: string | null, prev: string | null) {
        if (next && next !== prev) {
          this.getCurrencyRatios()
          this.$nextTick(() => {
            this.$bus.emit('selectedCurrenciesChanged')
          })
        }
      },
    },
    currencyFiat: {
      handler(next: Currency | null, prev: Currency | null) {
        if (next && JSON.stringify(next) !== JSON.stringify(prev)) {
          this.$nextTick(() => {
            this.$bus.emit('selectedFiatChanged')
          })
        }
      },
    },
    currencyCrypto: {
      handler(next: Currency | null, prev: Currency | null) {
        if (next && JSON.stringify(next) !== JSON.stringify(prev)) {
          this.$nextTick(() => {
            this.$bus.emit('selectedCryptoChanged')
          })
        }
      },
    },
    allCurrencies: {
      handler(next, prev) {
        if (next && next !== prev) {
          this.checkClientDefaultCurrencies()
          this.$nextTick(() => {
            this.$bus.emit('currenciesListChanged')
          })
        }
      },
    },
    entities: {
      handler(next, prev) {
        if (next && next !== prev) {
          this.$nextTick(() => {
            this.$bus.emit('entitiesListChanged')
          })
        }
      },
    },
    async currentView(view: SessionRole | null, oldView: SessionRole | null) {
      if (view && view !== oldView) {
        this.routesReady = false

        setupRoutes(view)

        if (oldView === null) {
          // First load
          await this.$router.replace(
            location.pathname + location.search + location.hash
          )
        } else {
          await this.$router.replace('/')
        }

        this.routesReady = true
      }
    },
    session_started(started: boolean) {
      if (started) {
        this.loadGlobalData()
      }
    },
    permissions: {
      handler() {
        this.checkCurrentRouteAccess()
      },
    },
    packages: {
      handler() {
        this.checkCurrentRouteAccess()
      },
    },
  },

  created() {
    this.sessionInterval = setInterval(this.checkSession, 30000)

    this.$bus.on('showMaintenance', this.showMaintenance)

    window.addEventListener('visibilitychange', this.handleVisibilityChange)
    window.addEventListener('storage', this.handleStorageEvent)

    this.broadcast.on(EVENT_LOGGED_OUT, this.handleUserSessionChanged)
  },

  async mounted() {
    await ApiService.liveCheck()

    await this.userStore.initialize()

    markReady()

    this.$nextTick().then(() => {
      this.routesReady = true
    })
  },

  beforeUnmount() {
    if (this.sessionInterval) {
      clearInterval(this.sessionInterval)
    }

    this.$bus.off('showMaintenance', this.showMaintenance)

    window.removeEventListener('visibilitychange', this.handleVisibilityChange)
    window.removeEventListener('storage', this.handleStorageEvent)

    this.broadcast.off(EVENT_LOGGED_OUT, this.handleUserSessionChanged)
  },

  methods: {
    checkCurrentRouteAccess() {
      if (!this.session_started) {
        return
      }

      const currentRoute = this.$router.currentRoute.value
      const redirectTo = getRedirectRouteIfNoAccess(currentRoute)

      if (redirectTo) {
        this.$router.replace(redirectTo)
      }
    },
    checkSession() {
      if (!this.session_started) {
        return
      }

      // If modal is already showing, let it handle expiry
      if (this.showWarningModal) {
        return
      }

      const timeUntilExpiry = TokenService.getTimeUntilExpiry()

      // No expiration data available
      if (timeUntilExpiry === null) {
        return
      }

      // Already expired -- immediate logout, no modal
      if (timeUntilExpiry <= 0) {
        this.handleSessionExpired()
        return
      }

      // Show warning only in focused tab
      if (timeUntilExpiry <= 90 && document.visibilityState === 'visible') {
        this.showWarningModal = true
      }
    },
    handleSessionExtended() {
      this.showWarningModal = false
    },
    handleSessionExpired() {
      this.showWarningModal = false
      this.userStore.expireSessions()
    },
    async checkClientDefaultCurrencies() {
      const view = this.userStore.currentView
      if (
        (view === 'client' || view === 'assistant') &&
        this.allCurrencies.length
      ) {
        await this.userStore.getDefaultCurrencies()
      }
    },
    async loadGlobalData() {
      if (!this.session_started) {
        return
      }

      try {
        await Promise.all([
          this.globalStore.getGeneral(),
          this.globalStore.getAllCurrencies(),
        ])
      } catch (err) {
        this.$bus.emit('error', err)
      }
    },
    async getCurrencyRatios() {
      try {
        await this.fetchCurrencyRatios()
      } catch (err) {
        this.$bus.emit('error', err)
      }
    },
    handlerEntityChanged(next: Entity | null, prev: Entity | null) {
      if (next) {
        this.checkClientDefaultCurrencies()

        if (next && !this.testMode.isStatusForced()) {
          this.testMode.toggleByEntity(next.id)
        }

        if (JSON.stringify(next) !== JSON.stringify(prev)) {
          if (prev?.valuation_method !== next.valuation_method) {
            this.getCurrencyRatios()
          }
          this.$nextTick(() => {
            this.$bus.emit('entityChanged')
          })
        }
      }
    },
    showMaintenance() {
      this.globalStore.showMaintenance = true
    },
    setLastSession() {
      if (document.visibilityState == 'visible') {
        const { currentView, entity } = this.userStore
        if (currentView) {
          UserService.setLastSession(currentView, entity?.id)
        }
      }
    },
    handleVisibilityChange() {
      if (document.visibilityState === 'visible') {
        // Immediate session check on tab focus or wake from sleep
        this.checkSession()
        // Preserve existing setLastSession behavior (saves user's last viewed role/entity)
        this.setLastSession()
      }
    },
    handleStorageEvent(event: StorageEvent) {
      // Session extension in another tab -- dismiss modal if session extended beyond warning zone
      if (event.key === REFRESH_TOKEN_EXPIRATION_KEY) {
        if (this.showWarningModal && event.newValue) {
          const newExpiration = Number(event.newValue)
          const now = Math.floor(Date.now() / 1000)
          if (newExpiration - now > 90) {
            this.showWarningModal = false
          }
        }
        return
      }

      // Login in another tab while we're logged out
      if (
        event.key === 'Authorization' &&
        event.newValue &&
        !this.session_started
      ) {
        location.reload()
        return
      }
    },
    handleUserSessionChanged() {
      if (sessionStorage.getItem('logout_initiated_by_me')) {
        sessionStorage.removeItem('logout_initiated_by_me')
        return
      }
      location.reload()
    },
  },
})
</script>
